import { useContext } from 'react';
import { AnnotationsOnPageManagerContext } from './AnnotationsOnPageManager.context';
import { AnnotationOnImage } from '../../models/annotation.types';
import useSessionUser from '../../hooks/useSessionUser';
import { useScoring } from '../../Context/Scoring.context';
import { canUserAssessAfterSendingBack } from '../../Context/helpers';
import { ExaminerFunction, VerificationState } from '../../pages/Assessment/Assessment.types';
import { SampleExamTaskGroupSolutionScoreState } from '../../services/scoringModel/scoringModel.types';
import { AssessmentSessionProcessTypeId } from '../../services/exams/assessmentSession/assessments.types';
import { VIEW_LEFT_MARGIN } from './previewConsts';
import { AnnotationImageData } from './Preview.types';
import { makeImageLocked, makeImageMovable } from './previewUtils';
import { ANNOTATION_OBJECT_NAME } from './annotationsConsts';

export const useAnnotations = () => {
  const context = useContext(AnnotationsOnPageManagerContext);
  if (!context) throw new Error('no <AnnotationsOnPageManagerContextProvider> wrapper');

  const { user, userAssessmentSessionInfo } = useSessionUser();
  const { examTaskGroupSolutionWithTasks, processTypeId } = useScoring();
  const {
    canvas,
    sampleExamTaskGroupSolutionScoreState,
    examinerFunction,
    getCanvasPoint,
    annotationImages,
    setAnnotationImages,
    activeCriterion,
    isActiveAnnotationForGrading,
    activeTask,
    addingAnnotationEnabled,
  } = context;

  const canUserEditRemoveAnnotation = (annotation: AnnotationOnImage) => {
    if (!addingAnnotationEnabled || activeTask?.noAnswer) return false;

    const annotationAuthorId = annotation.createdByExaminerId ?? annotation.examinerId;
    const isVerifierInScoringProcess =
      !!userAssessmentSessionInfo?.isVerifier && Number(processTypeId) === AssessmentSessionProcessTypeId.Scoring;

    if (isVerifierInScoringProcess) {
      return annotationAuthorId === user.profile.sub;
    }

    return (
      annotationAuthorId === user.profile.sub ||
      (examTaskGroupSolutionWithTasks &&
        examTaskGroupSolutionWithTasks.examTaskGroupSolutionScoreState !== null &&
        canUserAssessAfterSendingBack(
          examinerFunction,
          examTaskGroupSolutionWithTasks.examTaskGroupSolutionScoreState,
          examTaskGroupSolutionWithTasks.assessmentQuality !== null,
          !!userAssessmentSessionInfo?.isVerifier,
        )) ||
      (annotation.examinerFunctionWithZwo === ExaminerFunction.CZWO &&
        sampleExamTaskGroupSolutionScoreState === SampleExamTaskGroupSolutionScoreState.ChairmanAccepted) ||
      (examinerFunction === ExaminerFunction.PZWO &&
        sampleExamTaskGroupSolutionScoreState === SampleExamTaskGroupSolutionScoreState.DuringChairmanAcceptance) ||
      (examinerFunction === ExaminerFunction.KK &&
        sampleExamTaskGroupSolutionScoreState &&
        sampleExamTaskGroupSolutionScoreState > SampleExamTaskGroupSolutionScoreState.DuringChairmanAcceptance)
    );
  };

  const canUserRemoveAnnotation = (annotation: AnnotationOnImage) => {
    if (!addingAnnotationEnabled || activeTask?.noAnswer) return false;

    const isVerifierInScoringProcess =
      !!userAssessmentSessionInfo?.isVerifier &&
      Number(processTypeId) === AssessmentSessionProcessTypeId.Scoring &&
      examTaskGroupSolutionWithTasks?.verificationStateId !== VerificationState.scoreSelected;

    if (isVerifierInScoringProcess) {
      return true;
    }

    return canUserEditRemoveAnnotation(annotation);
  };

  const getCanvasPointsForAnnotation = (x: number, y: number) => {
    return getCanvasPoint(x + VIEW_LEFT_MARGIN, y);
  };

  const lockAllAnnotations = () => {
    canvas.discardActiveObject();
    canvas.getObjects().forEach((img) => {
      if (img.name?.includes(ANNOTATION_OBJECT_NAME)) {
        makeImageLocked(img);
      }
    });
    canvas.renderAll();
  };

  const unlockAllAnnotations = () => {
    annotationImages.forEach((annotationData: AnnotationImageData) => makeImageMovable(annotationData.image));
    canvas.renderAll();
  };

  const isAnnotationScoringRequired =
    activeCriterion &&
    ((activeCriterion.isScoringAnnotationRequired && isActiveAnnotationForGrading) ||
      !isActiveAnnotationForGrading ||
      (!activeCriterion.isScoringAnnotationRequired && !isActiveAnnotationForGrading)) &&
    activeTask &&
    addingAnnotationEnabled;

  return {
    ...context,
    isAnnotationScoringRequired,
    annotationImages,
    canUserEditRemoveAnnotation,
    canUserRemoveAnnotation,
    getCanvasPointsForAnnotation,
    lockAllAnnotations,
    setAnnotationImages,
    unlockAllAnnotations,
  };
};
